import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Sparkles, CalendarDays } from "lucide-react";
import { getLang } from "../lib/i18n";
import { openBooking } from "../lib/booking";
import { useBotGuard } from "../lib/botguard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUGGESTIONS = [
  "I want to launch a brokerage",
  "I want to start a prop firm",
  "What are your prices?",
];
const SUGGESTIONS_ES = [
  "Quiero abrir un bróker",
  "Quiero crear una prop firm",
  "¿Cuáles son sus precios?",
];

const AGENT_NAMES = {
  en: ["Daniel", "Sarah", "Omar", "Layla", "Adrian", "Maya", "Karim", "Elena"],
  es: ["Sofía", "Diego", "Valentina", "Andrés", "Camila", "Mateo"],
};

const getAgentName = () => {
  let name = sessionStorage.getItem("ahjin_agent_name");
  if (!name) {
    const pool = AGENT_NAMES[getLang() === "es" ? "es" : "en"];
    name = pool[Math.floor(Math.random() * pool.length)];
    sessionStorage.setItem("ahjin_agent_name", name);
  }
  return name;
};

const getSessionId = () => {
  let id = sessionStorage.getItem("ahjin_chat_session");
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem("ahjin_chat_session", id);
  }
  return id;
};

const getVisitorId = () => {
  let id = localStorage.getItem("ahjin_visitor_id");
  if (!id) {
    id = `v-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("ahjin_visitor_id", id);
  }
  return id;
};

const ConciergeChat = () => {
  const bot = useBotGuard("chat");
  const location = useLocation();
  const agentName = getAgentName();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: "welcome",
      role: "assistant",
      content:
        getLang() === "es"
          ? `¡Hola! Soy ${agentName}, de Ah-Jin Group — bienvenido. ¿Con quién tengo el gusto? ¿Y prefieres español o inglés?`
          : `Hi! I'm ${agentName} from Ah-Jin Group — welcome. May I ask who I'm speaking with? And do you prefer English or Spanish?`,
    },
  ]);
  const nextId = useRef(0);
  const newId = () => `m-${++nextId.current}-${Date.now()}`;
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [nudge, setNudge] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (sessionStorage.getItem("ahjin_nudge_done")) return;
    const timer = setTimeout(() => setNudge(true), 20000);
    return () => clearTimeout(timer);
  }, []);

  const dismissNudge = () => {
    setNudge(false);
    sessionStorage.setItem("ahjin_nudge_done", "1");
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, busy, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { id: newId(), role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { session_id: getSessionId(), visitor_id: getVisitorId(), agent_name: agentName, message: msg, turnstile_token: bot.fields().turnstile_token, website: bot.fields().website });
      setMessages((m) => [...m, { id: newId(), role: "assistant", content: data.reply, book: !!data.book }]);
    } catch (e) {
      const detail =
        e.response?.status === 429
          ? "You've sent quite a few messages — please allow a moment before continuing."
          : "My apologies — I'm momentarily unavailable. Please use the contact form below.";
      setMessages((m) => [...m, { id: newId(), role: "assistant", content: detail }]);
    } finally {
      setBusy(false);
    }
  };

  if (location.pathname.startsWith("/admin")) return null;

  return (
    <>
      {/* Proactive nudge */}
      <AnimatePresence>
        {nudge && !open && (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 14 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="chat-nudge"
            className="concierge-teaser fixed bottom-36 md:bottom-24 right-4 md:right-6 z-[79] w-[260px] rounded-2xl border border-[#E5C56F]/25 p-4"
            style={{ background: "rgba(10,7,20,0.96)", boxShadow: "0 20px 60px -20px rgba(0,0,0,0.8)" }}
          >
            <button
              onClick={dismissNudge}
              data-testid="chat-nudge-close"
              className="absolute top-2.5 right-2.5 text-zinc-500 hover:text-zinc-200 transition-colors"
              aria-label="Dismiss"
            >
              <X size={13} />
            </button>
            <div className="flex items-center gap-2 text-[#E5C56F]">
              <Sparkles size={13} />
              <span className="text-[9px] tracking-[0.3em] uppercase">{getLang() === "es" ? "Asesor Ah-Jin" : "Ah-Jin Advisor"}</span>
            </div>
            <p className="mt-2 text-[12.5px] text-zinc-200 leading-relaxed">
              {getLang() === "es"
                ? "¿Lanzando un bróker o prop firm? Nuestro asesor de lanzamiento está en línea — respuestas en segundos."
                : "Launching a brokerage or prop firm? Our launch advisor is online — answers in seconds."}
            </p>
            <button
              onClick={() => { dismissNudge(); setOpen(true); }}
              data-testid="chat-nudge-open"
              className="mt-3 w-full rounded-full border border-purple-500/40 bg-purple-600/15 px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-purple-200 hover:bg-purple-600/25 transition-colors"
            >
              {getLang() === "es" ? "Hablar con el asesor" : "Chat with our advisor"}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating toggle */}
      <button
        onClick={() => { if (!open) dismissNudge(); setOpen((v) => !v); }}
        data-testid="concierge-toggle"
        className="concierge-launcher fixed bottom-20 md:bottom-6 right-4 md:right-6 z-[80] h-14 rounded-full flex items-center justify-center gap-2.5 w-14 md:w-auto md:px-6 transition-transform duration-300 hover:scale-105"
        style={{
          background: "linear-gradient(135deg, #2a1548, #0c0614)",
          border: "1px solid rgba(229,197,111,0.5)",
          boxShadow: "0 0 24px rgba(168,85,247,0.45)",
        }}
        aria-label="Ah-Jin AI Advisor"
      >
        {open ? <X size={20} className="text-[#FFE9A8]" /> : (
          <>
            <Sparkles size={18} className="text-[#FFE9A8]" />
            <span className="hidden md:inline text-[11px] font-semibold uppercase tracking-[0.22em] text-[#FFE9A8] whitespace-nowrap">
              {getLang() === "es" ? "Habla con un Agente" : "Ask an Agent"}
            </span>
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#0c0614]" />
          </>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            data-testid="concierge-panel"
            className="concierge-panel fixed bottom-36 md:bottom-24 right-4 sm:right-6 z-[80] w-[calc(100vw-2rem)] sm:w-[380px] max-h-[70vh] flex flex-col rounded-2xl overflow-hidden border border-white/10"
            style={{ background: "rgba(8,5,14,0.97)", boxShadow: "0 30px 80px -20px rgba(0,0,0,0.8)" }}
          >
            <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
              <Sparkles size={14} className="text-[#E5C56F]" />
              <div>
                <div className="font-display text-lg text-zinc-100 leading-none">{agentName} · Ah-Jin</div>
                <div className="text-[9px] tracking-[0.35em] uppercase text-emerald-300/90 mt-1 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  {getLang() === "es" ? "En línea · Responde en segundos" : "Online · Replies in seconds"}
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-[220px]" data-testid="concierge-messages">
              {messages.map((m) => (
                <div key={m.id} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[13px] leading-relaxed ${
                      m.role === "user"
                        ? "bg-purple-700/40 text-zinc-100 rounded-br-sm"
                        : "bg-white/[0.05] text-zinc-200 border border-white/10 rounded-bl-sm"
                    }`}
                  >
                    {m.content}
                  </div>
                  {m.book && (
                    <button
                      onClick={() => { setOpen(false); openBooking("chat"); }}
                      data-testid="chat-book-call-btn"
                      className="mt-2 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E5C56F] to-[#C9A64B] px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1a1405] hover:brightness-110 transition-all"
                    >
                      <CalendarDays size={13} />
                      {getLang() === "es" ? "Reservar mi sesión de $250" : "Book my $250 call"}
                    </button>
                  )}
                </div>
              ))}
              {busy && (
                <div className="flex justify-start" data-testid="concierge-typing">
                  <div className="bg-white/[0.05] border border-white/10 rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1.5">
                    {[0, 1, 2].map((d) => (
                      <span key={d} className="w-1.5 h-1.5 rounded-full bg-[#E5C56F]/80 animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
                    ))}
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {(getLang() === "es" ? SUGGESTIONS_ES : SUGGESTIONS).map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid="concierge-suggestion"
                    className="text-[11px] px-3 py-1.5 rounded-full border border-purple-500/30 text-purple-200 hover:bg-purple-600/20 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <div className="px-3">{bot.guard}</div>
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="p-3 border-t border-white/10 flex items-center gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask the concierge…"
                data-testid="concierge-input"
                className="flex-1 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2.5 text-[13px] text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-purple-500/50"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                data-testid="concierge-send"
                className="w-10 h-10 rounded-full flex items-center justify-center bg-purple-700 hover:bg-purple-600 disabled:opacity-40 transition-colors"
              >
                <Send size={15} className="text-white" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ConciergeChat;
